import { createTheme, type ThemeOptions } from '@mui/material/styles'
import { colors, typography, createTypographyStyle, spacing } from '../tokens'

/**
 * Archer Design System — Single Enterprise Theme
 *
 * Warm neutral palette with coral/orange accent.
 * One consistent, professional look.
 *
 * NOTE: Function name kept as createIgnisTheme for backward compatibility.
 * This theme now powers the Archer platform.
 */

export const createIgnisTheme = () => {
  const themeOptions: ThemeOptions = {
    palette: {
      mode: 'light',
      primary: {
        main: colors.accent.main,
        light: colors.accent.light,
        dark: colors.accent.hover,
        contrastText: colors.accent.contrast,
      },
      secondary: {
        main: colors.neutral[800],
        light: colors.neutral[600],
        dark: colors.neutral[900],
        contrastText: colors.text.onDark,
      },
      success: {
        main: colors.success.main,
        light: colors.success.light,
        dark: colors.success.dark,
        contrastText: colors.text.onPrimary,
      },
      warning: {
        main: colors.warning.main,
        light: colors.warning.light,
        dark: colors.warning.dark,
        contrastText: colors.text.onPrimary,
      },
      error: {
        main: colors.error.main,
        light: colors.error.light,
        dark: colors.error.dark,
        contrastText: colors.text.onPrimary,
      },
      info: {
        main: colors.info.main,
        light: colors.info.light,
        dark: colors.info.dark,
        contrastText: colors.text.onPrimary,
      },
      background: {
        default: colors.background.page,
        paper: colors.background.card,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
        disabled: colors.text.disabled,
      },
      divider: colors.border.primary,
      action: {
        hover: colors.overlay.medium,
        selected: colors.accent.light,
        focus: colors.accent.light,
        disabled: colors.text.disabled,
        disabledBackground: colors.neutral[100],
      },
    },

    typography: {
      fontFamily: typography.fontFamily.primary,
      h1: { ...createTypographyStyle('pageTitle'), color: colors.text.primary },
      h2: { ...createTypographyStyle('sectionTitle'), color: colors.text.primary },
      h3: { ...createTypographyStyle('workspaceTitle'), color: colors.text.primary },
      h4: { ...createTypographyStyle('cardTitle'), fontFamily: typography.fontFamily.display, color: colors.text.primary },
      h5: { ...createTypographyStyle('subtitle'), color: colors.text.primary },
      h6: { ...createTypographyStyle('label'), color: colors.text.secondary },
      body1: { ...createTypographyStyle('body'), fontFamily: typography.fontFamily.primary, color: colors.text.primary },
      body2: { ...createTypographyStyle('bodySmall'), fontFamily: typography.fontFamily.primary, color: colors.text.secondary },
      caption: { ...createTypographyStyle('caption'), color: colors.text.tertiary },
      button: { ...createTypographyStyle('button'), textTransform: 'none' as const },
      overline: { ...createTypographyStyle('captionSmall'), color: colors.text.tertiary },
    },

    spacing: spacing.base,

    shape: {
      borderRadius: parseInt(spacing.borderRadius.md),
    },

    shadows: [
      'none',
      spacing.shadow.xs,
      spacing.shadow.sm,
      spacing.shadow.md,
      spacing.shadow.lg,
      spacing.shadow.xl,
      spacing.shadow.card,
      spacing.shadow.cardHover,
      spacing.shadow.dropdown,
      spacing.shadow.modal,
      ...Array(15).fill(spacing.shadow.lg),
    ] as any,

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { background: colors.background.page },
          body: {
            background: colors.background.page,
            color: colors.text.primary,
            minHeight: '100vh',
          },
        },
      },

      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: spacing.borderRadius.button,
            padding: spacing.component.buttonPadding,
            fontWeight: typography.fontWeight.medium,
            fontSize: typography.fontSize.md,
            textTransform: 'none',
            boxShadow: 'none',
            transition: `all ${spacing.transitions.fast} ${spacing.transitions.easing}`,
            '&:hover': { boxShadow: 'none' },
          },
          contained: {
            backgroundColor: colors.accent.main,
            color: colors.accent.contrast,
            '&:hover': {
              backgroundColor: colors.accent.hover,
            },
          },
          containedSecondary: {
            backgroundColor: colors.neutral[800],
            color: colors.text.onDark,
            '&:hover': {
              backgroundColor: colors.neutral[700],
            },
          },
          outlined: {
            borderColor: colors.border.primary,
            color: colors.text.primary,
            '&:hover': {
              borderColor: colors.border.hover,
              backgroundColor: colors.background.cardHover,
            },
          },
          text: {
            color: colors.text.primary,
            '&:hover': {
              backgroundColor: colors.overlay.medium,
            },
          },
          sizeSmall: {
            padding: spacing.component.buttonPaddingSmall,
            fontSize: typography.fontSize.sm,
          },
          sizeLarge: {
            padding: spacing.component.buttonPaddingLarge,
            fontSize: typography.fontSize.lg,
          },
        },
      },

      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: spacing.borderRadius.card,
            backgroundColor: colors.background.card,
            border: `1px solid ${colors.border.primary}`,
            boxShadow: spacing.shadow.card,
            transition: `all ${spacing.transitions.fast} ${spacing.transitions.easing}`,
            '&:hover': {
              boxShadow: spacing.shadow.cardHover,
            },
          },
        },
      },

      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: spacing.borderRadius.lg,
            backgroundColor: colors.background.card,
            boxShadow: 'none',
          },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: spacing.borderRadius.input,
              backgroundColor: colors.background.input,
              '& fieldset': {
                borderColor: colors.border.input,
              },
              '&:hover fieldset': {
                borderColor: colors.border.hover,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.accent.main,
                borderWidth: '2px',
              },
            },
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: typography.fontSize.md,
            lineHeight: 1.5,
          },
          head: {
            fontWeight: typography.fontWeight.semibold,
          },
        },
      },

      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: colors.background.header,
            borderBottom: `1px solid ${colors.border.primary}`,
            boxShadow: 'none',
            color: colors.text.primary,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          // Temporary drawers are modal portals. Keep them above expanded work
          // surfaces while persistent navigation retains the drawer layer.
          root: ({ ownerState, theme }) => ownerState.variant === 'temporary'
            ? { zIndex: theme.zIndex.modal }
            : {},
          paper: {
            backgroundColor: colors.background.sidebar,
            borderRight: `1px solid ${colors.border.primary}`,
            width: spacing.component.sidebarWidth,
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: spacing.borderRadius.chip,
            fontWeight: typography.fontWeight.medium,
            fontSize: typography.fontSize.xs,
          },
          filled: {
            backgroundColor: colors.neutral[100],
            color: colors.text.primary,
          },
          outlined: {
            borderColor: colors.border.primary,
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.neutral[800],
            color: colors.text.onDark,
            fontSize: typography.fontSize.xs,
            lineHeight: 1.5,
            borderRadius: spacing.borderRadius.sm,
            padding: '6px 12px',
          },
          arrow: {
            color: colors.neutral[800],
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: spacing.borderRadius.xl,
            boxShadow: spacing.shadow.modal,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: typography.fontWeight.medium,
            fontSize: typography.fontSize.md,
            color: colors.text.secondary,
            '&.Mui-selected': {
              color: colors.accent.main,
            },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: colors.accent.main,
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: colors.accent.main,
              '& + .MuiSwitch-track': {
                backgroundColor: colors.accent.main,
              },
            },
          },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: spacing.borderRadius.full,
            backgroundColor: colors.neutral[200],
          },
          bar: {
            borderRadius: spacing.borderRadius.full,
            backgroundColor: colors.accent.main,
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          // Quiet, production-grade alerts: a neutral surface with a single colored
          // left-accent + colored icon, instead of a saturated background flood.
          root: {
            borderRadius: spacing.borderRadius.md,
            backgroundColor: colors.background.surface,
            color: colors.text.primary,
            border: `1px solid ${colors.neutral[200]}`,
            boxShadow: 'none',
          },
          standardSuccess: {
            borderLeft: `3px solid ${colors.success.main}`,
            '& .MuiAlert-icon': { color: colors.success.main },
          },
          standardWarning: {
            borderLeft: `3px solid ${colors.warning.main}`,
            '& .MuiAlert-icon': { color: colors.warning.main },
          },
          standardError: {
            borderLeft: `3px solid ${colors.error.main}`,
            '& .MuiAlert-icon': { color: colors.error.main },
          },
          standardInfo: {
            borderLeft: `3px solid ${colors.info.main}`,
            '& .MuiAlert-icon': { color: colors.info.main },
          },
        },
      },
    },
  }

  return createTheme(themeOptions)
}

export const ignisTheme = createIgnisTheme()

export default createIgnisTheme
